import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import validator from 'validator';
import { useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createUser, updateUser } from '../redux/slices/clientUsersSlice';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

const empty = (value) => validator.isEmpty(value, { ignore_whitespace: true });

function ClientUsersDrawer({ open, onClose, user }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    if (user) {
      const { email, phone, first_name, last_name, role, status } = user;
      setEmail(email);
      setPhone(phone || '');
      setFirstName(first_name || '');
      setLastName(last_name || '');
      setSelectedRole(role);
      setSelectedStatus(status);
    }
  }, [user]);

  const createDisabled = useMemo(() => !!(empty(selectedRole) || !validator.isEmail(email)), [email, selectedRole]);

  const updateDisabled = useMemo(() => !!(empty(selectedRole) || !validator.isEmail(email)), [email, selectedRole]);

  const onDrawerClose = () => {
    onClose();
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  const onCreateUser = () => {
    const payload = {
      email,
      phone,
      first_name: firstName,
      last_name: lastName,
      role: selectedRole,
      status: selectedStatus,
    };

    dispatch(createUser(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('User created successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const onUpdateUser = () => {
    const payload = {
      user_id: user.id,
      email,
      phone,
      first_name: firstName,
      last_name: lastName,
      role: selectedRole,
      status: selectedStatus,
    };

    dispatch(updateUser(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('User updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  return (
    <Drawer anchor="right" open={open}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">{!user ? t('Create user') : t('Edit user')}</Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ width: 400, pr: 2, pl: 2 }}>
        <TextField
          value={firstName || ''}
          onChange={(event) => setFirstName(event.target.value)}
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          type="text"
          label={t('First name')}
        />
        <TextField
          value={lastName || ''}
          onChange={(event) => setLastName(event.target.value)}
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          type="text"
          label={t('Last name')}
        />
        <TextField
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          type="email"
          label={t('Email')}
        />
        <TextField
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          type="phone"
          label={t('Phone')}
        />
        <TextField
          value={selectedStatus}
          onChange={(event) => setSelectedStatus(event.target.value)}
          select
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          label={t('Status')}
        >
          <MenuItem value="ACTIVE">{t('ACTIVE')}</MenuItem>
          <MenuItem value="INACTIVE">{t('INACTIVE')}</MenuItem>
        </TextField>
        <TextField
          value={selectedRole}
          onChange={(event) => setSelectedRole(event.target.value)}
          select
          variant="standard"
          margin="normal"
          size="small"
          required={!user}
          fullWidth
          label={t('Role')}
        >
          <MenuItem value="CLIENT">{t('CLIENT')}</MenuItem>
        </TextField>
      </Box>
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Grid columnSpacing={2} container>
          <Grid xs={6} item>
            <Button fullWidth variant="outlined" onClick={onDrawerClose}>
              {t('Cancel')}
            </Button>
          </Grid>
          <Grid xs={6} item>
            <Button
              disabled={!user ? createDisabled : updateDisabled}
              fullWidth
              variant="contained"
              onClick={!user ? onCreateUser : onUpdateUser}
            >
              {!user ? t('Create') : t('Update')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

export default ClientUsersDrawer;
